// Imports estáticos dos logos para o Vite
import logoFull from "@/assets/images/logos/ori-logo-full.svg";
import logoIcon from "@/assets/images/logos/ori-logo-icon.svg";
import logoText from "@/assets/images/logos/ori-logo-text.svg";

// Imports estáticos dos backgrounds de orixás para o Vite (otimizados em JPG)
import iansaBackground from "@/assets/images/backgrounds/iansa.jpg";
import ibejiBackground from "@/assets/images/backgrounds/ibeji.jpg";
import iemanjaBackground from "@/assets/images/backgrounds/iemanja.jpg";
import nanaBackground from "@/assets/images/backgrounds/nana.jpg";
import ogumBackground from "@/assets/images/backgrounds/ogum.jpg";
import omuluBackground from "@/assets/images/backgrounds/omulu.jpg";
import oxalaBackground from "@/assets/images/backgrounds/oxala.jpg";
import oxossiBackground from "@/assets/images/backgrounds/oxossi.jpg";
import oxumBackground from "@/assets/images/backgrounds/oxum.jpg";
import xangoBackground from "@/assets/images/backgrounds/xango.jpg";

/**
 * Composable para gerenciar assets do projeto (versão com imports estáticos)
 */
export function useStaticAssets() {
	/**
	 * Obtém a URL de um logo específico usando imports estáticos
	 * @param logoKey - Chave do logo (full, icon, text)
	 * @returns URL do logo
	 */
	const getLogo = (logoKey: string): string => {
		switch (logoKey) {
			case "full":
				return logoFull;
			case "icon":
				return logoIcon;
			case "text":
				return logoText;
			default:
				console.warn(`Logo não encontrado: ${logoKey}`);
				return "";
		}
	};

	/**
	 * Obtém informações do logo
	 * @param logoKey - Chave do logo
	 * @returns Objeto com URL e alt text
	 */
	const getLogoInfo = (logoKey: string) => {
		const logoUrl = getLogo(logoKey);
		const altTexts = {
			full: "ORI - Sistema de Gerenciamento Completo",
			icon: "ORI - Ícone",
			text: "ORI - Texto",
		};

		return {
			url: logoUrl,
			alt: altTexts[logoKey as keyof typeof altTexts] || "ORI Logo",
		};
	};

	/**
	 * Obtém a URL de um background específico usando imports estáticos
	 * @param backgroundKey - Chave do background (iansa, ibeji, iemanja, etc.)
	 * @returns URL do background
	 */
	const getBackground = (backgroundKey: string): string => {
		switch (backgroundKey) {
			case "iansa":
				return iansaBackground;
			case "ibeji":
				return ibejiBackground;
			case "iemanja":
				return iemanjaBackground;
			case "nana":
				return nanaBackground;
			case "ogum":
				return ogumBackground;
			case "omulu":
				return omuluBackground;
			case "oxala":
				return oxalaBackground;
			case "oxossi":
				return oxossiBackground;
			case "oxum":
				return oxumBackground;
			case "xango":
				return xangoBackground;
			default:
				console.warn(`Background não encontrado: ${backgroundKey}`);
				return "";
		}
	};

	/**
	 * Lista de todas as chaves de backgrounds disponíveis
	 */
	const availableBackgrounds = [
		"iansa",
		"ibeji",
		"iemanja",
		"nana",
		"ogum",
		"omulu",
		"oxala",
		"oxossi",
		"oxum",
		"xango",
	];

	/**
	 * Obtém um background aleatório dos orixás
	 * @returns URL do background selecionado aleatoriamente
	 */
	const getRandomBackground = (): string => {
		const randomIndex = Math.floor(Math.random() * availableBackgrounds.length);
		const selectedBackground = availableBackgrounds[randomIndex];
		return getBackground(selectedBackground);
	};

	/**
	 * Obtém informações de um background específico
	 * @param backgroundKey - Chave do background
	 * @returns Objeto com URL e alt text
	 */
	const getBackgroundInfo = (backgroundKey: string) => {
		const backgroundUrl = getBackground(backgroundKey);

		const altTexts = {
			iansa: "Background de Iansã",
			ibeji: "Background de Ibeji",
			iemanja: "Background de Iemanjá",
			nana: "Background de Nanã",
			ogum: "Background de Ogum",
			omulu: "Background de Omulu",
			oxala: "Background de Oxalá",
			oxossi: "Background de Oxóssi",
			oxum: "Background de Oxum",
			xango: "Background de Xangô",
		};

		return {
			url: backgroundUrl,
			alt:
				altTexts[backgroundKey as keyof typeof altTexts] ||
				"Background de Orixá",
		};
	};

	return {
		getLogo,
		getLogoInfo,
		getBackground,
		getRandomBackground,
		getBackgroundInfo,
		availableBackgrounds,
	};
}
